import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react'
import {
  getSession,
  signIn as authSignIn,
  signUp as authSignUp,
  signOut as authSignOut,
  signInWithOAuth as authSignInWithOAuth,
  resetPassword as authResetPassword,
  updatePassword as authUpdatePassword,
  onAuthStateChange,
  refreshSession
} from '../services/AuthService'
import { checkAdminStatus } from '../services/CoreService'
import postHogService from '../services/PostHogService'

const TOKEN_REFRESH_INTERVAL_MS = 12 * 60 * 1000 // 12 minutes (access tokens expire in 15 min)

const AuthContext = createContext({})

export const useAuth = () => {
  return useContext(AuthContext)
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isAdmin, setIsAdmin] = useState(false)
  const [adminLoading, setAdminLoading] = useState(true)
  const refreshIntervalRef = useRef(null)

  // Function to handle automatic signout
  const handleAutoSignout = useCallback(async () => {
    console.warn('Session expired or refresh failed, signing out automatically')
    setUser(null)
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current)
      refreshIntervalRef.current = null
    }
    await authSignOut()
  }, [])

  // Function to refresh the session
  const attemptRefreshSession = useCallback(async () => {
    try {
      const newSession = await refreshSession()
      if (newSession?.user) {
        setUser({
          ...newSession.user,
          access_token: newSession.access_token
        })
        return true
      } else {
        await handleAutoSignout()
        return false
      }
    } catch (error) {
      console.error('Failed to refresh session:', error)
      await handleAutoSignout()
      return false
    }
  }, [handleAutoSignout])

  useEffect(() => {
    // Get initial session
    const getInitialSession = async () => {
      try {
        const session = await getSession()
        if (session?.user) {
          // Add access_token to user object for compatibility
          setUser({
            ...session.user,
            access_token: session.access_token
          })
          
          // Set up periodic token refresh (every 12 minutes to be safe with 15 minute tokens)
          const interval = setInterval(() => {
            attemptRefreshSession()
          }, TOKEN_REFRESH_INTERVAL_MS)
          refreshIntervalRef.current = interval
        } else {
          setUser(null)
        }
      } catch (error) {
        console.error('Failed to get initial session:', error)
        setUser(null)
      } finally {
        setLoading(false)
      }
    }

    getInitialSession()

    // Listen for auth changes
    const subscription = onAuthStateChange(
      async (event, session) => {
        console.log('Auth state changed:', event)
        
        if (event === 'TOKEN_REFRESHED') {
          if (session?.user) {
            setUser({
              ...session.user,
              access_token: session.access_token
            })
          }
        } else if (event === 'SIGNED_IN') {
          if (session?.user) {
            setUser({
              ...session.user,
              access_token: session.access_token
            })
            
            // Set up periodic token refresh
            if (refreshIntervalRef.current) {
              clearInterval(refreshIntervalRef.current)
            }
            const interval = setInterval(() => {
              attemptRefreshSession()
            }, TOKEN_REFRESH_INTERVAL_MS)
            refreshIntervalRef.current = interval
          }
        } else if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
          setUser(null)
          if (refreshIntervalRef.current) {
            clearInterval(refreshIntervalRef.current)
            refreshIntervalRef.current = null
          }
        } else if (!session) {
          // No session means token expired or invalid
          await handleAutoSignout()
        }
        setLoading(false)
      }
    )

    return () => {
      subscription.unsubscribe()
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current)
      }
    }
  }, [attemptRefreshSession, handleAutoSignout])

  // Check admin status when user changes
  useEffect(() => {
    let active = true

    if (!user?.id) {
      setIsAdmin(false)
      setAdminLoading(false)
      return () => { active = false }
    }

    setAdminLoading(true)
    checkAdminStatus()
      .then(result => { if (active) setIsAdmin(Boolean(result)) })
      .catch(() => { if (active) setIsAdmin(false) })
      .finally(() => { if (active) setAdminLoading(false) })

    return () => { active = false }
  }, [user?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const signUp = async (email, password, metadata = {}) => {
    const result = await authSignUp(email, password, metadata)

    // Only set user state if there's a valid session
    // When email confirmation is required, user exists but session is null
    if (result.data?.session && result.data?.user) {
      setUser({
        ...result.data.user,
        access_token: result.data.session.access_token
      })

      // Track sign-up event
      postHogService.trackEvent('user_signed_up', {
        method: 'email',
        user_id: result.data.user.id
      })

      // Set up periodic token refresh
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current)
      }
      const interval = setInterval(() => {
        attemptRefreshSession()
      }, TOKEN_REFRESH_INTERVAL_MS)
      refreshIntervalRef.current = interval
    }

    return result
  }

  const signIn = async (email, password) => {
    const result = await authSignIn(email, password)

    // Manually update user state after successful login (needed for JWT auth)
    if (result.data?.user) {
      setUser({
        ...result.data.user,
        access_token: result.data.session?.access_token || result.data.user.access_token
      })

      // Track sign-in event
      postHogService.trackEvent('user_signed_in', {
        method: 'email',
        user_id: result.data.user.id
      })

      // Set up periodic token refresh
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current)
      }
      const interval = setInterval(() => {
        attemptRefreshSession()
      }, TOKEN_REFRESH_INTERVAL_MS)
      refreshIntervalRef.current = interval
    }

    return result
  }

  const signInWithOAuth = async (provider) => {
    return authSignInWithOAuth(provider)
  }

  const signOut = async () => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current)
      refreshIntervalRef.current = null
    }
    // Mark this as an explicit logout (not just session expiry)
    sessionStorage.setItem('explicitLogout', 'true')
    // Clear any saved redirect URLs from sessionStorage
    sessionStorage.removeItem('redirectAfterLogin')
    sessionStorage.removeItem('authMessage')
    await authSignOut()
    setUser(null)
  }

  const resetPassword = async (email) => {
    return authResetPassword(email)
  }

  const updatePassword = async (password) => {
    return authUpdatePassword(password)
  }

  const value = {
    user,
    loading,
    isAdmin,
    adminLoading,
    signUp,
    signIn,
    signInWithOAuth,
    signOut,
    resetPassword,
    updatePassword
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
} 